/**
 * 숫자 포맷팅 유틸리티 함수
 */

/**
 * 숫자에 천단위 콤마를 추가합니다
 * @param {string|number} value - 포맷팅할 값
 * @returns {string} 콤마가 추가된 문자열
 */
export const addCommas = (value) => {
    if (!value && value !== 0) return '';

    // 숫자가 아닌 문자 제거 (소수점은 유지)
    const cleanValue = value.toString().replace(/[^\d.]/g, '');

    // 빈 문자열이면 빈 문자열 반환
    if (!cleanValue) return '';

    // 소수점이 있는 경우와 없는 경우 분리 처리
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    const decimalPart = parts[1];

    // 정수 부분에 콤마 추가
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // 소수점이 있으면 소수점 부분도 포함
    return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/**
 * 문자열에서 콤마를 제거합니다
 * @param {string} value - 콤마를 제거할 문자열
 * @returns {string} 콤마가 제거된 문자열
 */
export const removeCommas = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
};

/**
 * 입력값이 유효한 숫자인지 확인합니다
 * @param {string} value - 확인할 값
 * @param {number} maxLength - 최대 자릿수 (콤마 제외)
 * @returns {boolean} 유효한 숫자인지 여부
 */
export const isValidNumber = (value, maxLength = 10) => {
    if (!value) return true;

    const cleanValue = removeCommas(value);

    // 숫자가 아닌 문자가 있는지 확인 (소수점 제외)
    if (!/^\d*\.?\d*$/.test(cleanValue)) return false;

    // 최대 자릿수 확인 (소수점 제외)
    const digitCount = cleanValue.replace('.', '').length;
    return digitCount <= maxLength;
};

/**
 * 입력 필드용 숫자 포맷팅 핸들러
 * @param {string} value - 입력값
 * @param {number} maxLength - 최대 자릿수
 * @returns {string} 포맷팅된 값
 */
export const formatInputValue = (value, maxLength = 10) => {
    if (!value) return '';

    // 콤마 제거 후 유효성 검사
    const cleanValue = removeCommas(value);

    if (!isValidNumber(cleanValue, maxLength)) {
        return value; // 유효하지 않으면 이전 값 유지
    }

    return addCommas(cleanValue);
};

/**
 * 입력 필드의 onChange 이벤트를 처리하는 헬퍼 함수
 * @param {Event} e - 이벤트 객체
 * @param {Function} setValue - 상태 업데이트 함수
 * @param {number} maxLength - 최대 자릿수
 */
export const handleNumberInputChange = (e, setValue, maxLength = 10) => {
    const inputValue = e.target.value;
    const cleanValue = removeCommas(inputValue);

    // 유효성 검사
    if (isValidNumber(cleanValue, maxLength)) {
        setValue(addCommas(cleanValue));
    }
};

/**
 * 계산을 위해 숫자 값을 추출합니다
 * @param {string} formattedValue - 포맷팅된 값
 * @returns {number} 숫자 값
 */
export const getNumericValue = (formattedValue) => {
    const cleanValue = removeCommas(formattedValue);
    return cleanValue ? parseFloat(cleanValue) : 0;
};
